/**
 * Enhanced Auth API Service
 * Handles all /api/auth/* endpoints with proper error handling
 */

import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, from, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Auth } from '@angular/fire/auth';
import { environment } from '../../environments/environment';
import { 
  GetUserAccountRequest,
  GetUserAccountWithStatusResponse,
  RegisterUserRequest,
  RegisterUserResponse,
  ApiResult,
} from '../types/auth.types';
import { ErrorHandlingService } from './error-handling.service';

@Injectable({
  providedIn: 'root'
})
export class AuthApiService {
  private readonly baseUrl = `${environment.apiUrl}`;

  constructor(
    private http: HttpClient,
    private auth: Auth,
    private errorHandling: ErrorHandlingService
  ) {
    console.log('🔧 AuthAPI service initialized with base URL:', this.baseUrl);
  }

  /**
   * Get HTTP headers with authorization token
   */
  private async getHeaders(): Promise<HttpHeaders> {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });
    
    if (this.auth.currentUser) {
      try {
        const token = await this.auth.currentUser.getIdToken();
        headers = headers.set('Authorization', `Bearer ${token}`);
        console.log('🔑 Added auth token to request headers');
      } catch (error) {
        console.warn('⚠️ Failed to get ID token:', error);
        throw new Error('Authentication failed. Please sign in again.');
      }
    } else {
      console.warn('⚠️ No authenticated user found');
      throw new Error('Please sign in to continue.');
    }
    
    return headers;
  }

  /**
   * Handle API errors consistently
   */
  private handleError = (error: HttpErrorResponse): Observable<never> => {
    console.error('❌ API Error:', error);
    
    const userFriendlyError = this.errorHandling.convertToUserFriendlyError(error);
    
    // Add the user-friendly error to the original error object
    const enhancedError = {
      ...error,
      userFriendlyError
    };
    
    return throwError(() => enhancedError);
  };

  /**
   * Register user with the backend API
   * POST /api/auth/register
   */
  registerUser(userData: RegisterUserRequest): Observable<ApiResult<RegisterUserResponse>> {
    console.log('🚀 Registering user:', { mobile: userData.mobile, email: userData.email });
    
    return from(this.getHeaders()).pipe(
      switchMap(headers => 
        this.http.post<RegisterUserResponse>(`${this.baseUrl}/auth/register`, userData, { headers })
          .pipe(
            map(response => ({
              success: true,
              data: response
            } as ApiResult<RegisterUserResponse>)),
            catchError(error => {
              console.error('❌ Registration failed:', error);
              const userFriendlyError = this.errorHandling.convertToUserFriendlyError(error);
              return throwError(() => ({
                success: false,
                error: error.error || error,
                userFriendlyError
              }));
            })
          )
      )
    );
  }

  /**
   * Get user account by email or mobile
   * POST /api/auth/account
   */
  getUserAccount(): Observable<ApiResult<GetUserAccountWithStatusResponse>> {
    console.log('🔍 Fetching user account...');
    return from(this.getHeaders()).pipe(
      switchMap(headers => 
        this.http.get<GetUserAccountWithStatusResponse>(`${this.baseUrl}/auth/account`, { headers })
          .pipe(
            map(response => {
              console.log('✅ User account lookup response:', response);
              return {
                success: true,
                data: response
              } as ApiResult<GetUserAccountWithStatusResponse>;
            }),
            catchError(error => {
              console.error('❌ User account lookup failed:', error);
              
              // For any HTTP error (non-200), treat as a login error, not unregistered
              const userFriendlyError = this.errorHandling.convertToUserFriendlyError(error);
              return throwError(() => ({
                success: false,
                error: error.error || error,
                userFriendlyError,
                isHttpError: true // Flag to identify HTTP errors
              }));
            })
          )
      )
    );
  }

  /**
   * Check if user exists by mobile or email (convenience method)
   */
  checkUserExists(): Observable<ApiResult<boolean>> {
    return this.getUserAccount().pipe(
      map(result => ({
        success: true,
        data: result.success && result.data?.status !== 'unregistered'
      } as ApiResult<boolean>)),
      catchError(error => {
        // For any errors, return false instead of propagating error
        console.warn('⚠️ Error checking user existence, assuming user does not exist:', error);
        return [{ success: true, data: false } as ApiResult<boolean>];
      })
    );
  }

  /**
   * Validate mobile number format
   */
  validateMobileNumber(mobile: string): { isValid: boolean; error?: string } {
    // Remove all non-digits
    const cleanMobile = mobile.replace(/\D/g, '');
    
    if (cleanMobile.length === 0) {
      return { isValid: false, error: 'Mobile number is required' };
    }
    
    // Check for minimum length (adjust based on your requirements)
    if (cleanMobile.length < 10) {
      return { isValid: false, error: 'Mobile number must be at least 10 digits' };
    }
    
    // Check for maximum length
    if (cleanMobile.length > 15) {
      return { isValid: false, error: 'Mobile number cannot exceed 15 digits' };
    }
    
    return { isValid: true };
  }

  /**
   * Validate email format
   */
  validateEmail(email: string): { isValid: boolean; error?: string } {
    if (!email || email.trim().length === 0) {
      return { isValid: false, error: 'Email is required' };
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return { isValid: false, error: 'Please enter a valid email address' };
    }
    
    return { isValid: true };
  }

  /**
   * Prepare registration data with validation
   */
  prepareRegistrationData(data: {
    mobile: string;
    email?: string;
    name?: string;
    type?: 'individual' | 'agency' | 'corporate';
    emailVerified?: boolean;
    mobileVerified?: boolean;
    agency?: any;
    corporate?: any;
  }): { isValid: boolean; data?: RegisterUserRequest; errors?: string[] } {
    const errors: string[] = [];
    
    // Validate mobile
    const mobileValidation = this.validateMobileNumber(data.mobile);
    if (!mobileValidation.isValid) {
      errors.push(mobileValidation.error!);
    }
    
    // Validate email if provided
    if (data.email) {
      const emailValidation = this.validateEmail(data.email);
      if (!emailValidation.isValid) {
        errors.push(emailValidation.error!);
      }
    }
    
    // Validate name if provided
    if (data.name && data.name.trim().length < 2) {
      errors.push('Name must be at least 2 characters long');
    }
    
    if (errors.length > 0) {
      return { isValid: false, errors };
    }
    
    // Prepare clean data
    const registrationData: RegisterUserRequest = {
      mobile: data.mobile.replace(/\D/g, ''), // Clean mobile number
      email: data.email?.trim() || undefined,
      name: data.name?.trim() || undefined,
      type: data.type || 'individual',
      emailVerified: data.emailVerified || false,
      mobileVerified: data.mobileVerified || false,
      agency: data.agency,
      corporate: data.corporate
    };
    
    return { isValid: true, data: registrationData };
  }

  /**
   * Get error message for UI display
   */
  getErrorMessage(error: any): string {
    return this.errorHandling.getErrorMessage(error);
  }

  /**
   * Check if error requires login redirect
   */
  shouldRedirectToLogin(error: any): boolean {
    return this.errorHandling.shouldRedirectToLogin(error);
  }
}

// Helper function to import switchMap
import { switchMap } from 'rxjs/operators';